import { LIMITS, api, extensionOf, isAllowedAttachment, type FileInfo } from '@sd/contracts';
import { ClientModule, errorCode, type ClientContext } from './context';
import type { Outbox } from './outbox';
import type { TrayItem } from './state';

/** The composer's attachment tray: upload → scan → send. */
export class Uploads extends ClientModule {
  private readonly queue: Array<() => Promise<void>> = [];
  private active = 0;
  /** Picked files by tray item, kept until the item leaves the tray so a failed upload can be retried. */
  private readonly files = new Map<string, File>();
  /** Set by the client: after files are sent, the conversation shows its latest messages again. */
  afterSend: (conversationId: string) => void = () => {};

  constructor(
    ctx: ClientContext,
    private readonly outbox: Outbox,
  ) {
    super(ctx);
  }

  /** Adds files to the conversation's tray within the server's limits (size per file, files per send). */
  addFiles(conversationId: string, files: readonly File[]) {
    const limits = this.state.serverInfo?.limits ?? LIMITS;
    const tray = this.state.trays[conversationId] ?? [];
    const room = limits.maxFilesPerMessage - tray.length;
    if (files.length > room) this.ctx.notice({ kind: 'upload.rejected', name: String(files.length - Math.max(0, room)), reason: 'too_many' });
    const accepted: Array<{ item: TrayItem; file: File }> = [];
    for (const file of files.slice(0, Math.max(0, room))) {
      if (file.size > limits.maxFileBytes) {
        this.ctx.notice({ kind: 'upload.rejected', name: file.name, reason: 'too_large' });
        continue;
      }
      if (!isAllowedAttachment(file.name) && this.state.serverInfo?.limits.executablesAllowed !== true) {
        this.ctx.notice({ kind: 'upload.rejected', name: file.name, reason: 'not_allowed' });
        continue;
      }
      const item: TrayItem = {
        localId: this.ctx.newId(),
        conversationId,
        name: file.name,
        ext: extensionOf(file.name),
        size: file.size,
        state: 'uploading',
        progress: 0,
        fileId: null,
        file: null,
        error: null,
      };
      accepted.push({ item, file });
    }
    if (!accepted.length) return;
    // Store first: queued tasks skip items that are no longer in the tray.
    this.set((s) => ({ trays: { ...s.trays, [conversationId]: [...(s.trays[conversationId] ?? []), ...accepted.map((a) => a.item)] } }));
    for (const { item, file } of accepted) {
      this.files.set(item.localId, file);
      this.queueUpload(() => this.uploadItem(item, file));
    }
  }

  /** Uploads a failed tray file again; the server forgot the failed upload, so it starts over. */
  retryTrayItem(localId: string) {
    const item = this.findTrayItem(localId);
    const file = this.files.get(localId);
    if (!item || !file || item.state !== 'failed') return;
    this.patchTray(localId, { state: 'uploading', progress: 0, fileId: null, file: null, error: null });
    this.queueUpload(() => this.uploadItem(item, file));
  }

  private queueUpload(task: () => Promise<void>) {
    this.queue.push(task);
    this.pumpUploads();
  }

  private pumpUploads() {
    while (this.active < LIMITS.maxConcurrentUploadsPerUser && this.queue.length) {
      const task = this.queue.shift()!;
      this.active += 1;
      void task().finally(() => {
        this.active -= 1;
        this.pumpUploads();
      });
    }
  }

  private patchTray(localId: string, patch: Partial<TrayItem>) {
    this.set((s) => {
      for (const [conversationId, items] of Object.entries(s.trays)) {
        const index = items.findIndex((i) => i.localId === localId);
        if (index >= 0) {
          const next = [...items];
          next[index] = { ...items[index]!, ...patch };
          return { trays: { ...s.trays, [conversationId]: next } };
        }
      }
      return {};
    });
  }

  private findTrayItem(localId: string): TrayItem | undefined {
    for (const items of Object.values(this.state.trays)) {
      const item = items.find((i) => i.localId === localId);
      if (item) return item;
    }
    return undefined;
  }

  private async uploadItem(item: TrayItem, file: File) {
    if (!this.findTrayItem(item.localId)) return; // removed while queued
    const abort = this.timers.abortable(`upload:${item.localId}`);
    let uploadId: string | null = null;
    try {
      const created = await this.api.call(api.files.createUpload, {
        body: { conversationId: item.conversationId, fileName: item.name, size: item.size, ...(file.type ? { mimeType: file.type } : {}) },
      });
      uploadId = created.uploadId;
      // Removed from the tray while the upload was being created.
      abort.signal.throwIfAborted();
      const res = await this.api.upload(api.files.putUploadContent, { params: { uploadId }, body: file }, {
        signal: abort.signal,
        onProgress: (sent, total) => this.patchTray(item.localId, { progress: total ? Math.min(99, Math.round((sent / total) * 100)) : 0 }),
      });
      this.patchTray(item.localId, { fileId: res.file.id, file: res.file, progress: 100 });
      this.applyScannedFile(res.file);
    } catch (error) {
      if (abort.signal.aborted) {
        if (uploadId) void this.api.call(api.files.cancelUpload, { params: { uploadId } }).catch(() => {});
        return;
      }
      const code = errorCode(error);
      this.patchTray(item.localId, { state: 'failed', error: code });
      this.ctx.notice({ kind: 'upload.rejected', name: item.name, reason: code === 'FILE_TOO_LARGE' ? 'too_large' : code === 'FILE_TYPE_NOT_ALLOWED' ? 'not_allowed' : 'failed' });
      this.sendWhenReady(item.conversationId);
    } finally {
      this.timers.release(`upload:${item.localId}`, abort);
    }
  }

  /** Maps a scan result onto tray items and pending attachments. */
  applyScannedFile(file: FileInfo) {
    const state = trayStateOf(file);
    for (const [conversationId, items] of Object.entries(this.state.trays)) {
      for (const item of items) {
        if (item.fileId !== file.id) continue;
        this.patchTray(item.localId, { state, file });
        if (state === 'scanning') this.pollScan(item.localId);
        else this.timers.clear(`scan:${item.localId}`);
        if (state === 'infected') this.ctx.notice({ kind: 'upload.rejected', name: item.name, reason: 'infected' });
        if (state !== 'scanning') this.sendWhenReady(conversationId);
      }
    }
  }

  /**
   * Files a restart brought back into the tray, checked once: the scan may have
   * finished meanwhile, a send saved too late to the outbox may already carry
   * the file, and the server removes unsent files a day after their scan.
   */
  checkRestored() {
    for (const items of Object.values(this.state.trays)) {
      for (const item of items) {
        const fileId = item.fileId;
        if (!fileId) continue;
        this.api.call(api.files.get, { params: { fileId } }).then(
          (res) => {
            if (this.stopped) return;
            if (res.file.messageId) this.removeTrayItem(item.conversationId, item.localId);
            else this.applyScannedFile(res.file);
          },
          (error: unknown) => {
            if (!this.stopped && errorCode(error) === 'NOT_FOUND') this.patchTray(item.localId, { state: 'failed', fileId: null, file: null, error: 'ATTACH_AGAIN' });
          },
        );
      }
    }
  }

  /** Polls again for files still being scanned, e.g. after a reconnect. */
  resumeScans() {
    for (const items of Object.values(this.state.trays)) for (const item of items) if (item.state === 'scanning' && item.fileId) this.pollScan(item.localId);
  }

  private pollScan(localId: string) {
    const name = `scan:${localId}`;
    if (this.timers.has(name)) return;
    const started = Date.now();
    this.timers.every(name, 3000, () => {
      const item = this.findTrayItem(localId);
      if (!item?.fileId || item.state !== 'scanning' || Date.now() - started > 120_000) {
        this.timers.clear(name);
        return;
      }
      this.api
        .call(api.files.get, { params: { fileId: item.fileId } })
        .then((res) => this.applyScannedFile(res.file))
        .catch(() => {});
    });
  }

  /** Uploads still waiting for a slot never start once the client is stopped; running ones are aborted with the timers. */
  stop() {
    this.queue.length = 0;
    this.files.clear();
  }

  removeTrayItem(conversationId: string, localId: string) {
    this.timers.abort(`upload:${localId}`);
    this.timers.clear(`scan:${localId}`);
    this.files.delete(localId);
    this.set((s) => ({ trays: { ...s.trays, [conversationId]: (s.trays[conversationId] ?? []).filter((i) => i.localId !== localId) } }));
    // The removed file may have been the last one the others were waiting for.
    this.sendWhenReady(conversationId);
  }

  /**
   * "파일 보내기": the files that are ready go now, images together in one
   * message (grid) and other files one message each, like the design.
   * Unscannable files are sent with their warning. Files still uploading or
   * being scanned are marked and go together once the last of them is done.
   */
  sendTray(conversationId: string): { sent: number; waiting: number } {
    const tray = this.state.trays[conversationId] ?? [];
    const ready = tray.filter((i) => (i.state === 'ready' || i.state === 'unscannable') && i.file);
    const busy = tray.filter((i) => i.state === 'uploading' || i.state === 'scanning');
    if (busy.length) {
      const marked = new Set(busy.map((i) => i.localId));
      this.set((s) => ({ trays: { ...s.trays, [conversationId]: (s.trays[conversationId] ?? []).map((i) => (marked.has(i.localId) ? { ...i, sendWhenReady: true } : i)) } }));
    }
    this.sendItems(conversationId, ready);
    return { sent: ready.length, waiting: busy.length };
  }

  /**
   * Sends the marked files once none of them is uploading or being scanned any
   * more. Only clean files go: the sender never saw the warning of a file that
   * turned out unscannable, so it stays in the tray for them to decide. Failed
   * and infected files stay too (each already got its notice).
   */
  private sendWhenReady(conversationId: string) {
    const marked = (this.state.trays[conversationId] ?? []).filter((i) => i.sendWhenReady);
    if (!marked.length || marked.some((i) => i.state === 'uploading' || i.state === 'scanning')) return;
    const ready = marked.filter((i) => i.state === 'ready' && i.file);
    const held = marked.filter((i) => i.state === 'unscannable').map((i) => i.name);
    const unmark = new Set(marked.map((i) => i.localId));
    this.set((s) => ({ trays: { ...s.trays, [conversationId]: (s.trays[conversationId] ?? []).map((i) => (unmark.has(i.localId) ? { ...i, sendWhenReady: false } : i)) } }));
    this.sendItems(conversationId, ready);
    if (ready.length || held.length) this.ctx.notice({ kind: 'upload.sent_when_ready', sent: ready.length, held });
  }

  private sendItems(conversationId: string, items: TrayItem[]) {
    if (!items.length) return;
    const images = items.filter((i) => i.file!.kind === 'image');
    const others = items.filter((i) => i.file!.kind !== 'image');
    if (images.length) this.outbox.sendMessage(conversationId, '', { attachments: images.map((i) => i.file!) });
    for (const item of others) this.outbox.sendMessage(conversationId, '', { attachments: [item.file!] });
    const sent = new Set(items.map((i) => i.localId));
    for (const localId of sent) this.files.delete(localId);
    this.set((s) => ({ trays: { ...s.trays, [conversationId]: (s.trays[conversationId] ?? []).filter((i) => !sent.has(i.localId)) } }));
    this.afterSend(conversationId);
  }
}

export function trayStateOf(file: FileInfo): TrayItem['state'] {
  switch (file.scan) {
    case 'clean':
      return 'ready';
    case 'unscannable':
      return 'unscannable';
    case 'infected':
      return 'infected';
    case 'error':
      return 'failed';
    default:
      return 'scanning';
  }
}
